import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  EditableReportQuestion,
  QuestionDescriptionDTO,
  TemplateEditableSection,
} from "../../../types/Rows";
import { buildQuestionTree, byWeight, useTemplateContext } from "../templateContext";
import { DndProvider } from "react-dnd";
import update from "immutability-helper";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DraggableItem } from "../DragAndDrop/DraggableItem";
import { Row } from "./questionsListEdit";
import { Button } from "../../../components/ui/button";
import { ItemTypes } from "../DragAndDrop/ItemTypes";
import { TiArrowSortedUp, TiArrowSortedDown } from "react-icons/ti";
import { deleteSection } from "../../../api/reportsApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import AddItemWithExistingPicker, { InputType } from "../AddItemWithExistingPicker";

// Pytanie z pytaniami potomnymi (do 2 poziomów w głąb)
const QuestionItem = ({
  templateId,
  sectionId,
  isLocked,
  item,
  index,
  depth = 0,
}: {
  templateId: string;
  sectionId: string;
  isLocked: boolean;
  item: EditableReportQuestion;
  index: number;
  depth?: number;
}) => (
  <>
    <Row templateId={templateId} index={index} input={item} sectionId={sectionId} isLocked={isLocked} depth={depth} />
    {depth < 2 &&
      item.children?.map((child, i) => (
        <div className="pl-10" key={child.id}>
          <QuestionItem templateId={templateId} sectionId={sectionId} isLocked={isLocked} item={child} index={i} depth={depth + 1} />
        </div>
      ))}
  </>
);

export const SectionEdit = ({
  section,
  items,
  templateId,
  isLocked,
  index,
  isFirst,
  isLast,
  onMove,
  existingItems,
  saveNew,
  saveExisting,
}: {
  section: TemplateEditableSection;
  items: EditableReportQuestion[];
  templateId: string;
  isLocked: boolean;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  onMove: (direction: -1 | 1) => void;
  existingItems: QuestionDescriptionDTO[];
  saveNew: (sectionId: string, descriptionId: string) => void;
  saveExisting: (sectionId: string, questionId: string) => void;
}) => {
  const { updateSection, questionWeights, setQuestionOrder } = useTemplateContext();
  const [name, setName] = useState<string>(section.name);
  const queryClient = useQueryClient();

  // Pytania najwyższego poziomu w kolejności: niezapisana (po przeciągnięciu) albo z serwera.
  // Po odświeżeniu danych (dodanie/usunięcie pytania) lista się aktualizuje bez przeładowania edytora.
  const roots = useMemo(() => buildQuestionTree(items), [items]);
  const [currentItems, setCurrentItems] = useState(() => [...roots].sort(byWeight(questionWeights)));
  useEffect(() => {
    setCurrentItems([...roots].sort(byWeight(questionWeights)));
    // kolejność z mapy czytamy tylko przy nowych danych; przeciąganie aktualizuje listę samo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots]);

  // Po przeciągnięciu: zapamiętaj kolejność całej sekcji (weight = pozycja od 1)
  const moved = useRef(false);
  const moveCard = useCallback((dragIndex: number, hoverIndex: number) => {
    moved.current = true;
    setCurrentItems((prev) =>
      update(prev, { $splice: [[dragIndex, 1], [hoverIndex, 0, prev[dragIndex]]] })
    );
  }, []);
  useEffect(() => {
    if (!moved.current) return;
    moved.current = false;
    setQuestionOrder(currentItems);
  }, [currentItems, setQuestionOrder]);

  // Nazwa sekcji: do zapisu tylko po zmianie
  const nameTouched = useRef(false);
  useEffect(() => {
    if (!nameTouched.current) return;
    updateSection({ ...section, name } as TemplateEditableSection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const { mutate: deleteSec } = useMutation(async () => await deleteSection(templateId, section.id), {
    onSuccess: () => {
      queryClient.invalidateQueries(["Templates", templateId]);
    },
  });

  const arrowClass =
    "p-0.5 rounded-[4px] text-[var(--sc-muted)] bg-transparent border-none cursor-pointer hover:text-[var(--sc-text)] hover:bg-[var(--sc-border)] disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent";

  return (
    <div id={`section-${section.id}`} className="bg-[var(--sc-surface)] border border-[var(--sc-border)] rounded-[12px] overflow-hidden">
      {/* Section header */}
      <div className="flex items-center gap-[10px] px-4 py-3 bg-[var(--sc-bg)] border-b border-[var(--sc-border)]">
        {!isLocked && (
          <span className="flex flex-col -my-1">
            <button type="button" aria-label="Przesuń sekcję wyżej" title="Przesuń wyżej" disabled={isFirst} onClick={() => onMove(-1)} className={arrowClass}>
              <TiArrowSortedUp size={14} />
            </button>
            <button type="button" aria-label="Przesuń sekcję niżej" title="Przesuń niżej" disabled={isLast} onClick={() => onMove(1)} className={arrowClass}>
              <TiArrowSortedDown size={14} />
            </button>
          </span>
        )}
        <span className="bg-[var(--sc-text)] text-[var(--sc-surface)] text-[10px] font-semibold w-5 h-5 rounded-[4px] flex items-center justify-center flex-shrink-0">
          {index + 1}
        </span>
        <input
          type="text"
          placeholder="Nazwa sekcji"
          value={name}
          disabled={isLocked}
          onChange={(e) => {
            nameTouched.current = true;
            setName(e.target.value);
          }}
          className="flex-1 text-[13px] font-semibold text-[var(--sc-text)] uppercase tracking-[0.05em] border-none bg-transparent outline-none disabled:opacity-60 focus:bg-[var(--sc-bg)] focus:rounded-[4px] focus:px-[6px] focus:py-[2px] transition-all"
        />
        <span className="text-[10px] text-[var(--sc-muted)] whitespace-nowrap">{items.length} pytań</span>
        {!isLocked && (
          <button
            className="text-[11px] text-[var(--sc-muted)] px-2 py-1 rounded-[4px] border border-[var(--sc-border)] bg-transparent hover:text-[#C03030] hover:border-[#F0D0D0] transition-colors"
            onClick={async () => {
              const msg = items.length > 0
                ? `Usunąć sekcję „${name}” razem z ${items.length} pytaniami?`
                : `Usunąć sekcję „${name}”?`;
              if (window.confirm(msg)) {
                await deleteSec();
              }
            }}
          >
            Usuń sekcję
          </button>
        )}
      </div>

      {/* Questions */}
      <div>
        <DndProvider backend={HTML5Backend}>
          {currentItems.map((card, i) => (
            <DraggableItem
              itemType={ItemTypes.CARD}
              key={card.id}
              index={i}
              id={card.id}
              text={card.description}
              moveCard={moveCard}
              disabled={isLocked}
            >
              <QuestionItem item={card} sectionId={section.id} isLocked={isLocked} templateId={templateId} index={i} />
            </DraggableItem>
          ))}
        </DndProvider>
      </div>

      {/* Add question row */}
      {!isLocked && (
        <div className="border-t border-[var(--sc-border)] px-4 py-2.5 flex justify-end">
          <AddItemWithExistingPicker
            text="Dodaj pytanie"
            inputType={InputType.PICKFROMEXISTING}
            saveNew={(descriptionId) => saveNew(section.id, descriptionId)}
            saveExisting={(questionId) => saveExisting(section.id, questionId)}
            items={() =>
              Promise.resolve(
                existingItems.map((x) => ({ id: x.id, name: x.description })) ?? []
              )
            }
          >
            <Button size="sm">Dodaj pytanie</Button>
          </AddItemWithExistingPicker>
        </div>
      )}
    </div>
  );
};
